// src/pages/services/PillPeriodShiftPage.tsx
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaPills, FaInfoCircle, FaArrowRight, FaCalendarAlt, FaArrowLeft } from 'react-icons/fa';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { goReserve } from '../../utils/navigation';
import { useMedicinePrices, toSinglePriceItems } from '../../hooks/useMedicinePrices';

interface PillChildProps { ipAllowed: boolean }

type NoticeDoc = { title?: string; html?: string };

const NOTICE_VERSION = 'v2025-10-01';

function getPublicBase(): string {
  // @ts-ignore
  // eslint-disable-next-line
  const viteBase = process.env.PUBLIC_URL || '';
  // @ts-ignore
  const craBase = (typeof process !== 'undefined' && process?.env?.PUBLIC_URL) || '';
  const base = viteBase || craBase || '/';
  return base.endsWith('/') ? base.slice(0, -1) : base;
}
function normalizeLang(input: string): 'ja' | 'zh' {
  return String(input || '').toLowerCase().startsWith('zh') ? 'zh' : 'ja';
}

const PillPeriodShiftPage: React.FC<PillChildProps> = ({ ipAllowed }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const lang: 'ja' | 'zh' = normalizeLang(i18n.language);

  // 薬品価格（DB優先、フォールバックは静的データ）
  const { medicines } = useMedicinePrices('pill', lang);

  const [openNotice, setOpenNotice] = useState(false);
  const [agree, setAgree] = useState(false);
  const [doc, setDoc] = useState<NoticeDoc | null>(null);
  const [docLoading, setDocLoading] = useState(false);

  const openNoticeModal = async () => {
    setAgree(false);
    setOpenNotice(true);
    try {
      setDocLoading(true);
      const base = getPublicBase();
      const primary = `${base}/data/notices/pill_shift.${lang}.json?v=${encodeURIComponent(NOTICE_VERSION)}`;
      const fallback = `${base}/data/notices/pill_shift.${lang === 'ja' ? 'zh' : 'ja'}.json?v=${encodeURIComponent(
        NOTICE_VERSION
      )}`;
      const tryFetch = async (url: string) => {
        const res = await fetch(url, { cache: 'no-cache' });
        return res.ok ? ((await res.json()) as NoticeDoc) : null;
      };
      let json = await tryFetch(primary);
      if (!json) json = await tryFetch(fallback);
      setDoc(json || { title: lang === 'ja' ? '注意事項' : '注意事项', html: '' });
    } finally {
      setDocLoading(false);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenNotice(false); };
    if (openNotice) {
      window.addEventListener('keydown', onKey);
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        window.removeEventListener('keydown', onKey);
        document.body.style.overflow = prev;
      };
    }
  }, [openNotice]);

  const proceedReserve = () => {
    if (!agree) return;
    setOpenNotice(false);
    goReserve(navigate, ipAllowed, {
      preselectService: 'pill',
      preAgreed: true,
      from: 'service',
      pillNoticeScope: 'pill_shift',
    });
  };

  const L = {
    ja: {
      helmetTitle: '月経移動・避妊 - Melodis Clinic',
      back: '戻る',
      heading: '月経移動・避妊',
      badges: ['診察料 0円', '24時間オンライン予約', 'スピード発送', '自由診療'],
      overviewAndMethod: '概要と方法',
      intro:
        '旅行や試験など大切な予定と生理が重なりそうなとき、中用量ピル（プラノバール®）で生理日を調整できます。方法は「遅らせる」「早める」の2通りです。',
      delayTitle: '生理日を遅らせる',
      delayText:
        '生理予定日（最も早い日）の5日前から服用を開始し、生理を避けたい日まで1日1錠ずつ服用を継続. 中止後2〜5日で生理が始まります。服用中に出血が始まった場合はその時点で中止してください。',
      advanceTitle: '生理日を早める',
      advanceText:
        '生理開始5日以内（3日以内が望ましい）から服用を始め、1日1錠を10日間以上服用。終了2〜5日後から軽めの生理が始まります。遅らせる方法に比べ成功率は高めです。',
      priceTitle: '価格（診察料 0円）：',
      priceItems: [
        { name: 'プラノバール (Planovar)', dosage: '7片', price: '2,980', taxPrice: '3,278' },
        { name: 'プラノバール (Planovar)', dosage: '14片', price: '价格准备中', taxPrice: '-' },
        { name: 'プラノバール (Planovar)', dosage: '21片', price: '价格准备中', taxPrice: '-' },
      ],
      noticeBtn: '注意事項はこちらから',
      backToList: 'ピル一覧へ戻る',
      reserveCta: 'オンライン診療を予約する',
      modalAgree: '上記を読み、内容に同意します。',
      modalCta: '同意して予約に進む',
      modalClose: '閉じる',
    },
    zh: {
      helmetTitle: '月经移位・避孕 - Melodis Clinic',
      back: '返回',
      heading: '月经移位・避孕',
      badges: ['诊察费 0 日元', '24 小时在线预约', '急速发货', '自费诊疗'],
      overviewAndMethod: '概览与方法',
      intro:
        '当旅行、考试等重要行程可能与经期冲突时，可通过中剂量避孕药 (Planovar) 调整月经日期。方法分为“推迟”和“提前”两种。',
      delayTitle: '推迟月经',
      delayText:
        '从预计来潮（最早那天）的前5天开始服用，每日1片，持续服用至希望避开经期的日期。停药后2～5天一般会来月经；若用药期间已出现出血，则应在当时停止用药。',
      advanceTitle: '提前月经',
      advanceText:
        '在月经开始5天内（以3天内为佳）开始每日1片，连续服用至少10天。停药后2～5天通常会来较为轻微的月经。与“推迟”相比，此法成功率更高。',
      priceTitle: '价格（诊察费 0 日元）：',
      priceItems: [
        { name: '普拉诺芭尔 (Planovar)', dosage: '7片', price: '2,980', taxPrice: '3,278' },
        { name: '普拉诺芭尔 (Planovar)', dosage: '14片', price: '价格准备中', taxPrice: '-' },
        { name: '普拉诺芭尔 (Planovar)', dosage: '21片', price: '价格准备中', taxPrice: '-' },
      ],
      noticeBtn: '查看注意事项',
      reserveCta: '立即预约在线咨询',
      backToList: '返回避孕药一览',
      modalAgree: '我已阅读并同意上述内容。',
      modalCta: '同意并继续预约',
      modalClose: '关闭',
    },
  } as const;

  // 価格表は DB(admin設定)優先。'pill' サービスの薬品から本ページの薬(プラノバール)だけ名称で抽出。
  // price/taxPrice は本ページJSXが文字列前提のため 'ja-JP' 整形。空ならハードコード L[lang].priceItems にフォールバック。
  const dbPriceItems = toSinglePriceItems(medicines, lang)
    .filter((it) => /プラノバール|普拉诺|Planovar/i.test(it.name))
    .map((it) => ({ ...it, price: it.price.toLocaleString('ja-JP'), taxPrice: it.taxPrice.toLocaleString('ja-JP') }));
  const priceItems: any[] = dbPriceItems.length > 0 ? dbPriceItems : [...L[lang].priceItems];

  return (
    <div className="pt-0 sm:pt-10 pb-10 sm:pb-20 bg-[#fff9f8] font-sans" translate="no">
      <Helmet>
        <title>{L[lang].helmetTitle}</title>
        <meta name="description" content={t('seo.pill_period_shift.description')} />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <div className="container mx-auto px-0 sm:px-4 max-w-5xl">
        <div className="bg-white shadow-none sm:shadow-md rounded-none sm:rounded-3xl p-5 sm:p-8 md:p-12 border-y sm:border border-slate-100">

          {/* 上部：戻る / 返回 */}
          <div className="mb-4">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-800 text-sm"
            >
              <FaArrowLeft className="w-4 h-4" /> {L[lang].back}
            </button>
          </div>

          {/* 見出し / 标题 */}
          <div className="flex items-center mb-6">
            <div className="mr-4 rounded-2xl bg-amber-100 p-3">
              <FaPills className="text-amber-600 w-7 h-7" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-800">
              <span className="text-amber-500 mr-2">│</span>{L[lang].heading}
            </h1>
          </div>

          <div className="mb-8 flex items-center gap-3 text-left">
            <Link
              to="/about"
              className="inline-flex items-center gap-2 rounded-full bg-slate-50 border border-slate-200 px-4 py-1.5 text-xs md:text-sm text-slate-600 hover:bg-slate-100 transition"
            >
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              {t('seo.supervision_doctor')}
              <span className="text-[10px] text-slate-400 font-normal ml-1">{t('seo.supervision_title')}</span>
            </Link>
          </div>

          <p className="text-slate-700 text-base md:text-lg leading-relaxed mb-8 bg-[#fff9f8]/50 border-l-4 border-sky-200 pl-4 py-2">
            {t('seo.pill_period_shift.summary')}
          </p>

          {/* 概要＋方法 */}
          <section className="rounded-2xl border border-amber-200 bg-amber-50 p-6 md:p-8 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <FaInfoCircle className="text-amber-600 w-5 h-5" />
              <h2 className="text-2xl font-semibold text-amber-700">{L[lang].overviewAndMethod}</h2>
            </div>

            {/* badges */}
            <ul className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 text-sm">
              {L[lang].badges.map((b, i) => (
                <li key={i} className="rounded-xl border border-amber-200 bg-white px-4 py-3 text-slate-800 text-center shadow-sm">
                  {b}
                </li>
              ))}
            </ul>

            <div className="space-y-4 text-slate-800 leading-relaxed">
              <p>{L[lang].intro}</p>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="rounded-xl border border-slate-200 bg-white p-5">
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">
                    <FaCalendarAlt className="inline-block mr-2" /> {L[lang].delayTitle}
                  </h3>
                  <p className="text-slate-700">{L[lang].delayText}</p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-5">
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">
                    <FaCalendarAlt className="inline-block mr-2" /> {L[lang].advanceTitle}
                  </h3>
                  <p className="text-slate-700">{L[lang].advanceText}</p>
                </div>
              </div>

              {/* 価格＋リンク */}
              <div className="mt-2 rounded-xl border border-slate-200 bg-white p-5 md:p-8">
                <h3 className="text-xl font-semibold text-slate-800 mb-6">{L[lang].priceTitle}</h3>
                
                <ul className="space-y-4 text-slate-700">
                  {priceItems.map((item: any, i: number) => (
                    <li key={i} className="flex justify-between items-start flex-wrap gap-2 py-1 border-b border-slate-50 last:border-0 pb-3">
                      <span className="flex flex-col">
                        <span className="font-bold text-slate-800">{item.name}</span>
                        <span className="text-[11px] text-slate-500">{item.dosage}</span>
                      </span>
                      <div className="text-right">
                        <div className="flex items-baseline gap-1 justify-end">
                          <span className="font-bold text-lg text-slate-900">{item.price}</span>
                          {(item.price as string) !== '价格准备中' && (item.price as string) !== '価格準備中' && (
                            <span className="text-sm text-slate-600">{lang === 'ja' ? '円' : ' 日元'}</span>
                          )}
                        </div>
                        {(item.taxPrice as string) !== '-' && (
                          <div className="text-[10px] text-slate-400 font-normal">
                            ({lang === 'ja' ? '税込' : '含税'} {item.taxPrice}{lang === 'ja' ? '円' : ' 日元'})
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 flex flex-wrap gap-3">
                  <button
                    onClick={openNoticeModal}
                    className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-6 py-2.5 text-sm font-medium text-amber-700 hover:bg-amber-100 transition-colors shadow-sm"
                  >
                    {L[lang].noticeBtn} <FaArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* 下部：戻る／予約 CTA（→ モーダル経由） */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => navigate('/menu/pill')}
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3 text-slate-700 hover:bg-slate-50"
            >
              <FaArrowLeft className="w-4 h-4" /> {L[lang].backToList}
            </button>
            {isAuthenticated && (
              <button
                onClick={openNoticeModal}
                className="inline-block bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white px-8 py-3 rounded-full font-semibold shadow-md transition-all hover:-translate-y-0.5"
              >
                {L[lang].reserveCta}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 注意事項モーダル（pill_shift） */}
      {openNotice && (
        <div
          className="fixed inset-0 z-[1000] overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="notice-title"
          onClick={() => setOpenNotice(false)}
        >
          <div className="fixed inset-0 bg-black/40" />
          <div
            className="min-h-full flex items-start md:items-center justify-center p-4 md:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative z-[1001] w-full max-w-2xl bg-white rounded-2xl shadow-xl p-6 md:p-7 border border-slate-200 max-h-[90vh] overflow-y-auto">
              <div className="flex items-start justify-between gap-4 mb-3">
                <h3 id="notice-title" className="text-xl md:text-2xl font-semibold text-slate-900">
                  {doc?.title || (lang === 'ja' ? '注意事項' : '注意事项')}
                </h3>
                <button
                  onClick={() => setOpenNotice(false)}
                  aria-label="Close"
                  className="rounded-full px-3 py-1.5 bg-slate-100 text-slate-600 hover:bg-slate-200"
                >
                  ×
                </button>
              </div>

              <div className="prose prose-slate max-w-none text-[15px] leading-relaxed">
                {docLoading ? (
                  <p className="text-slate-500">{lang === 'ja' ? '読み込み中…' : '加载中…'}</p>
                ) : doc?.html ? (
                  <div dangerouslySetInnerHTML={{ __html: doc.html }} />
                ) : (
                  <p className="text-slate-500">
                    {lang === 'ja' ? 'この項目の注意事項は現在準備中です。' : '该项目的注意事项正在准备中。'}
                  </p>
                )}
              </div>

              <label className="mt-4 flex items-start gap-2 text-slate-700">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                />
                <span>{L[lang].modalAgree}</span>
              </label>

              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  disabled={!agree}
                  onClick={proceedReserve}
                  className={`inline-flex items-center justify-center rounded-full px-6 py-2.5 font-semibold text-white transition ${agree ? 'bg-amber-500 hover:bg-amber-600 active:bg-amber-700' : 'bg-amber-300 cursor-not-allowed'
                    }`}
                >
                  {L[lang].modalCta}
                </button>
                <button
                  onClick={() => setOpenNotice(false)}
                  className="rounded-full border border-slate-300 px-6 py-2.5 text-slate-700 hover:bg-slate-50"
                >
                  {L[lang].modalClose}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PillPeriodShiftPage;
